/**
 * ChatPanel — the Play page's main chat organism.
 *
 * Render path (the streaming-perf invariant):
 *   1. The finalized message list re-renders ONLY when a new turn finalizes,
 *      driven by `useChat().messages`. It is virtualized via TanStack
 *      Virtual so a long conversation does not put thousands of nodes in the
 *      DOM.
 *   2. The streaming row is a SEPARATE component that subscribes to
 *      `useChatStream(s => s.streamingByAgentId)` directly. Per-token re-renders
 *      are isolated to that one row — the virtualized list does not see them.
 *   3. The active-agents pill row uses a different selector
 *      (`useChatStream(s => s.agents)`) and only re-renders on phase
 *      transitions.
 *
 * ARIA: a single visually-hidden live region narrates streaming progress,
 * debounced 600 ms (per CLAUDE.md retro rule §7) — we don't bombard screen
 * readers with every token.
 *
 * Accessibility: keyboard parity — Tab through the messages, Enter to send,
 * Esc to clear focus from the composer (handled by `ChatComposer`).
 *
 * Post-ADR-001 (Tailwind v4 + Radix UI): no Cloudscape imports. The error
 * banner is a Tailwind div with red ink on a Forest surface, dismiss button
 * is a `lucide-react` X icon.
 */

import {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactElement,
} from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { X } from 'lucide-react';
import { useChatStream } from '@/stores/chatStream.store';
import { AGENTS, type AgentId } from '@/tokens/colors';
import MessageBubble from '@/components/molecules/MessageBubble';
import type { ToolInvocation } from '@/components/molecules/ToolInvocationChip';
import { useAnnounce } from '@/components/common/AccessibleAnnouncer';
import { useStreamingAgentIds } from '@/hooks/useStreamingAgentIds';
import { useStreamingBuffer } from '@/hooks/useStreamingBuffer';
import { useMultistreamSummary } from '@/hooks/useMultistreamSummary';
import { useFlag } from '@/contexts/FlagsContext';
import MarkdownContent from './MarkdownContent';
import type { ChatMessage, ChatMessageCard } from '@/hooks/useChat';
import { ChatTaskAckCard } from '@/components/molecules/ChatTaskAckCard';
import { ChatTaskLiveCard } from '@/components/molecules/ChatTaskLiveCard';
import { ChatTaskTerminalCard } from '@/components/molecules/ChatTaskTerminalCard';
import { ChatFullAutoConfirmCard } from '@/components/molecules/ChatFullAutoConfirmCard';
import { submitChatFullAuto } from '@/api/tasks';

export interface ChatPanelProps {
  readonly sessionId: string;
  readonly messages: readonly ChatMessage[];
  readonly errorBanner: string | null;
  readonly onClearError: () => void;
  readonly isStreaming: boolean;
  /** Render slot for the composer — `<ChatComposer />` lives below. */
  readonly composer: React.ReactNode;
}

const ESTIMATE_PX = 96;

export default function ChatPanel({
  sessionId,
  messages,
  errorBanner,
  onClearError,
  isStreaming,
  composer,
}: ChatPanelProps): ReactElement {
  const parentRef = useRef<HTMLDivElement | null>(null);

  // The virtualizer renders the finalized messages. The streaming row is
  // appended OUTSIDE the virtualizer so its re-renders never affect
  // the virtualizer's bookkeeping.
  const virtualizer = useVirtualizer({
    count: messages.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => ESTIMATE_PX,
    overscan: 6,
    getItemKey: (index) => messages[index]?.id ?? `idx-${index}`,
  });

  // Auto-scroll to the bottom when messages or streaming state changes —
  // unless the user has scrolled up to read history.
  const stickToBottomRef = useRef(true);
  useLayoutEffect(() => {
    const el = parentRef.current;
    if (!el) return;
    if (stickToBottomRef.current) {
      el.scrollTop = el.scrollHeight;
    }
  }, [messages.length, isStreaming]);

  const onScroll = useCallback((): void => {
    const el = parentRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    stickToBottomRef.current = distanceFromBottom < 24;
  }, []);

  const items = virtualizer.getVirtualItems();
  const isEmpty = messages.length === 0;

  return (
    <div
      data-testid="chat-panel"
      data-wayne-chat-panel
      data-session-id={sessionId}
      className="flex flex-col flex-1 min-h-0 h-full"
    >
      {errorBanner ? (
        <div
          role="alert"
          className="mx-3 mt-3 mb-1 flex items-start gap-2 px-3 py-2 rounded-lg border bg-wayne-surface"
          style={{
            borderColor: 'var(--wayne-red)',
            color: 'var(--wayne-red)',
          }}
        >
          <span className="flex-1 text-[13px] leading-[1.5]">{errorBanner}</span>
          <button
            type="button"
            onClick={onClearError}
            aria-label="Dismiss error"
            className="inline-flex items-center justify-center w-6 h-6 rounded hover:bg-wayne-surface-2 transition-colors"
            style={{ color: 'var(--wayne-red)' }}
          >
            <X size={14} aria-hidden="true" />
          </button>
        </div>
      ) : null}

      <div
        ref={parentRef}
        onScroll={onScroll}
        className="flex-1 min-w-0 min-h-0 overflow-y-auto px-3 py-2"
        role="log"
        aria-label="Conversation"
        aria-live="off"
      >
        {isEmpty ? (
          <div
            className="text-wayne-ink-3 italic px-2 py-4"
            style={{ fontSize: 13 }}
          >
            No messages yet.
          </div>
        ) : null}
        <div
          style={{
            position: 'relative',
            height: virtualizer.getTotalSize(),
            width: '100%',
          }}
        >
          {items.map((row) => {
            const msg = messages[row.index];
            if (!msg) return null;
            return (
              <div
                key={row.key}
                data-index={row.index}
                ref={virtualizer.measureElement}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  transform: `translateY(${row.start}px)`,
                  padding: '4px 0',
                }}
              >
                <ChatMessageRow message={msg} sessionId={sessionId} />
              </div>
            );
          })}
        </div>

        {/* Streaming rows — one per active agent, outside the virtualizer. */}
        <StreamingRows />
      </div>

      <div className="shrink-0 px-3 py-3 border-t border-wayne-hairline bg-wayne-surface">
        {composer}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Finalized-message row (memoized so the virtualizer doesn't churn)
// ---------------------------------------------------------------------------

const ChatMessageRow = memo(function ChatMessageRow({
  message,
  sessionId,
}: {
  readonly message: ChatMessage;
  readonly sessionId: string;
}): ReactElement {
  const [expanded, setExpanded] = useState(false);

  // Phase 3: render task cards inline when the message carries a `card`
  // payload. Card-typed messages bypass the regular MessageBubble path.
  if (message.card) {
    return <ChatCardRow card={message.card} sessionId={sessionId} />;
  }

  const baseProps = {
    role: message.role,
    timestamp: message.timestamp,
    content:
      message.role === 'agent' ? (
        <MarkdownContent text={message.content} />
      ) : (
        <span className="whitespace-pre-wrap break-words">
          {message.content}
        </span>
      ),
    ...(message.tools ? { toolCalls: message.tools } : {}),
    ...(message.errored ? { errored: true } : {}),
  } as const;

  const bubble =
    message.role === 'agent' && message.agent ? (
      <MessageBubble {...baseProps} agent={message.agent} />
    ) : (
      <MessageBubble {...baseProps} />
    );

  return (
    <div>
      {bubble}
      {message.contributingAgents && message.contributingAgents.length > 1 && (
        <div className="mt-2 pt-2 border-t border-wayne-hairline">
          <button
            type="button"
            className="text-xs text-wayne-ink-3 hover:text-wayne-ink-2 flex items-center gap-1"
            onClick={() => setExpanded(!expanded)}
            aria-expanded={expanded}
          >
            <span>{message.contributingAgents.length} agents contributed</span>
            <span aria-hidden="true">{expanded ? '▾' : '▸'}</span>
          </button>
          {expanded && (
            <div className="mt-1 flex flex-wrap gap-1">
              {message.contributingAgents.map((agentId) => {
                const identity = AGENTS[agentId as keyof typeof AGENTS];
                return identity ? (
                  <span
                    key={agentId}
                    className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-wayne-surface-2"
                  >
                    <span
                      className="w-1.5 h-1.5 rounded-full"
                      style={{ backgroundColor: identity.light }}
                      aria-hidden="true"
                    />
                    {identity.displayName}
                  </span>
                ) : null;
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
});

// ---------------------------------------------------------------------------
// Inline card row (Phase 3 — task.* / fullauto.confirm envelopes)
// ---------------------------------------------------------------------------

function ChatCardRow({
  card,
  sessionId,
}: {
  readonly card: ChatMessageCard;
  readonly sessionId: string;
}): ReactElement {
  switch (card.kind) {
    case 'task.ack':
      return (
        <ChatTaskAckCard
          runId={card.runId}
          title={card.title}
          agent={card.agent}
          packageName={card.packageName}
        />
      );
    case 'task.live':
      return <ChatTaskLiveCard runId={card.runId} />;
    case 'task.terminal':
      return (
        <ChatTaskTerminalCard
          runId={card.runId}
          title={card.title}
          outcome={card.outcome}
          summary={card.summary}
        />
      );
    case 'fullauto.confirm':
      return (
        <ChatFullAutoConfirmCard
          packageName={card.packageName}
          summary={card.summary}
          originalPrompt={card.originalPrompt}
          estimatedDurationMin={card.estimatedDurationMin}
          onConfirm={({ originalPrompt }) => {
            void submitChatFullAuto({
              sessionId,
              text: originalPrompt,
              source: 'confirm',
            });
          }}
          onEdit={() => {
            /* focus composer; no-op for now */
          }}
          onCancel={() => {
            /* dismiss; no-op for now */
          }}
        />
      );
    default: {
      const _exhaustive: never = card;
      void _exhaustive;
      return <></>;
    }
  }
}

// ---------------------------------------------------------------------------
// Streaming rows (per-token re-renders isolated here — one row per agent)
// ---------------------------------------------------------------------------

function StreamingRows(): ReactElement | null {
  const ids = useStreamingAgentIds();
  const enabled = useFlag('chat.multistream.enabled');

  // Coalesced count of agents currently responding (spec §2.5). This reflects
  // the real concurrency regardless of the kill-switch's visual projection.
  const { count } = useMultistreamSummary();

  // Debounced live-region narration (600ms per CLAUDE.md §7). The announcement
  // coalesces multiple concurrent agents into "<N> agents are responding"
  // rather than naming only the first. When the count DECREASES (an agent
  // finalized), we bypass the debounce so the screen reader hears the reduced
  // count promptly.
  const { announce } = useAnnounce();
  const lastAnnouncedAtRef = useRef(0);
  const lastCountRef = useRef(0);
  useEffect(() => {
    if (ids.length === 0) {
      lastCountRef.current = 0;
      return;
    }
    const now = Date.now();
    const finalized = count < lastCountRef.current;
    if (!finalized && now - lastAnnouncedAtRef.current < 600) {
      lastCountRef.current = count;
      return;
    }
    lastAnnouncedAtRef.current = now;
    lastCountRef.current = count;
    if (count > 1) {
      announce(`${count} agents are responding`, 'polite');
    } else {
      const id = ids[0]!;
      const display = AGENTS[id]?.displayName ?? id;
      announce(`${display} is responding`, 'polite');
    }
  }, [ids, count, announce]);

  if (ids.length === 0) return null;
  const visibleIds = enabled ? ids : ids.slice(-1); // singleton projection: most-recent only
  return (
    <>
      {visibleIds.map((id) => (
        <AgentStreamingRow key={id} agentId={id} />
      ))}
    </>
  );
}

function AgentStreamingRow({ agentId }: { agentId: AgentId }): ReactElement | null {
  const buffer = useStreamingBuffer(agentId);
  const agents = useChatStream((s) => s.agents);
  if (!buffer) return null;
  const agentRuntime = agents[agentId];
  const tools: ToolInvocation[] =
    agentRuntime && agentRuntime.currentTool
      ? [
          {
            id: `live-tool-${agentRuntime.currentTool}`,
            toolName: agentRuntime.currentTool,
            status: 'pending',
          },
        ]
      : [];
  return (
    <div
      data-testid={`wayne-streaming-row-${agentId}`}
      data-wayne-streaming-row
      data-agent={agentId}
      className="py-1"
    >
      <MessageBubble
        // eslint-disable-next-line jsx-a11y/aria-role -- `role` is the MessageBubble chat-role prop, not an ARIA role.
        role="agent"
        agent={agentId}
        streaming
        errored={buffer.errored}
        toolCalls={tools}
        content={
          <span className="whitespace-pre-wrap break-words">
            {buffer.text}
          </span>
        }
      />
    </div>
  );
}
